import React from 'react';
import { Link } from "react-router-dom";
import "../../../assets/css/landing.css";
import Hero from "../../structures/Hero";
import Footer from "../../structures/Footer";

const activities = [
  {
    id: '01',
    title: 'Bicycle Rental',
    desc: 'Explore the scenic lakeside trails on our premium mountain and road bikes. Feel the gentle breeze as you pedal through peaceful paths, discovering hidden corners of Lake Tondano\'s natural beauty.',
    price: '175K',
    duration: '1 – 2.5 Hours (±12 km)',
    features: [
      'High-quality mountain/road bike',
      'Professional local guide',
      '±1–2.5 hours journey (±12 km)',
      'Optional Drone Photo & Video: Rp 300.000',
    ],
    bestTime: 'Morning 05:30 · Afternoon 16:00',
    img: '/images/section5ahome_bicycling1.webp',
    imgAlt: 'Bicycle Path',
    reverse: true,
  },
  {
    id: '02',
    title: 'Paint on Pottery (Earthenware)',
    desc: 'Express your creativity by painting on earthenware pottery. With our complete equipment set, transform a plain pottery piece into your personal masterpiece.',
    price: '200K',
    duration: '2 Hours',
    features: [
      'Pottery media',
      'Complete equipment set',
      'Take home your creation',
      '2-hour session duration',
    ],
    img: '/images/section7ahome_painting2.webp',
    imgAlt: 'Painting Craft',
    reverse: false,
  },
  {
    id: '03',
    title: 'Pottery Class (Earthenware)',
    desc: 'Immerse yourself in the ancient art of pottery. Shape 1kg of earthenware clay into unique pieces under the guidance of skilled artisans.',
    price: '200K',
    duration: '2 Hours',
    features: [
      '1 kg premium clay',
      'Complete equipment set',
      'Instructor guidance',
      'Take home your creation',
      '2-hour session duration',
    ],
    img: '/images/section6ahome_pottery2.webp',
    imgAlt: 'Pottery Craft',
    reverse: true,
  },
  {
    id: '04',
    title: 'Pottery Class (Stoneware)',
    desc: 'Create premium stoneware pottery with a professional transparent glaze finish. Food grade and durable stoneware crafted from high-quality clay.',
    price: '300K',
    duration: '2 Hours',
    features: [
      '700g premium clay',
      'Complete equipment set',
      'Instructor guidance',
      'Transparent glaze finish',
      'Food-grade quality',
      'Take home your creation',
      '2-hour session duration',
    ],
    img: '/images/pottery/stoneware1.webp',
    imgAlt: 'Stoneware Pottery',
    reverse: false,
  },
];

function ActivitySection({ activity }) {
  const { id, title, desc, price, duration, features, img, imgAlt, reverse, packages, bestTime } = activity;

  return (
    <section className="section section--activity">
      <div className={`activity-grid${reverse ? ' activity-grid--reverse' : ''}`}>
        <div className="activity-grid__content">
          <span className="activity__label">Activity {id}</span>
          <h2 className="activity__title">{title}</h2>
          <p className="activity__desc">{desc}</p>

          {/* Dual-package layout (Bicycle) */}
          {packages ? (
            <>
              <div className="package-cards">
                {packages.map((pkg, i) => (
                  <div key={i} className={`package-card${pkg.highlight ? ' package-card--highlight' : ''}`}>
                    {pkg.highlight && <span className="package-badge">RECOMMENDED</span>}
                    <h4 className="package-card__label">{pkg.label}</h4>
                    <span className="package-card__price">{pkg.price} <span>/ orang</span></span>
                    <ul className="check-list check-list--compact">
                      {pkg.features.map((f, j) => (
                        <li key={j} className="check-list__item">
                          <span className="material-symbols-outlined">check</span>
                          {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
              {bestTime && (
                <div className="activity__best-time">
                  <span className="material-symbols-outlined">schedule</span>
                  Best time: {bestTime}
                </div>
              )}
            </>
          ) : (
            /* Single-price layout (other activities) */
            <>
              <div className="activity__price-row">
                <span className="activity__price">
                  {price} <span>/ pax</span>
                </span>
                <span className="activity__duration">{duration}</span>
              </div>
              <ul className="check-list">
                {features.map((f, i) => (
                  <li key={i} className="check-list__item">
                    <span className="material-symbols-outlined">check</span>
                    {f}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
        <div className="activity-grid__visual">
          <img className="activity__img" src={img} alt={imgAlt} />
        </div>
      </div>
    </section>
  );
}

export default function Landing() {
  return (
    <>
      {/* Section 1 - Hero */}
      <Hero
        useSlideshow={true}
        images={[
          '/images/section6ahome_pottery1.webp',
          '/images/section7ahome_painting2.webp',
          '/images/about1.webp',
          '/images/prewed/prewed2.jpg'
        ]}
      />

      {/* ── Introduction ─────────────────────────────────── */}
      <section className="section section--intro">
        <h2 className="intro__heading">Escape the noise and find your rhythm.</h2>
        <div className="intro__divider" />
        <p className="intro__body">
          At À L'AISE, we curate moments of intentional stillness beside the tranquil waters of
          Lake Tondano. Here, creativity and nature entwine in a sanctuary for the slow-living
          enthusiast.
        </p>
      </section>

      {/* ── Activities ───────────────────────────────────── */}
      {activities.map((activity) => (
        <ActivitySection key={activity.id} activity={activity} />
      ))}

      {/* ── Special Packages ────────────────────────────────── */}
      <section className="section section--combo">
        <span className="activity__label">Activity 05</span>
        <h2 className="combo__heading">Pottery Painting & Pottery Making (Earthenware)</h2>
        <p className="combo__desc">
          Can't decide? Immerse yourself in the full creative experience. Choose two activities and
          enjoy a curated afternoon of slow living.
        </p>
        <div className="combo__box">
          <span className="combo__price">
            350K <span>/ pax</span>
          </span>
          <span className="combo__note">3 Hours</span>
        </div>
        <p className="combo__desc" style={{ fontSize: '0.9rem', marginTop: '1rem', fontStyle: 'italic', color: 'var(--text-muted)' }}>
          Participants can paint their own pottery (requires 14–21 days drying) or paint on provided terracotta media.
        </p>
        <ul className="check-list" style={{ marginTop: '2rem', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <li className="check-list__item">
            <span className="material-symbols-outlined">check</span>
            1 kg premium clay
          </li>
          <li className="check-list__item">
            <span className="material-symbols-outlined">check</span>
            1 terracotta media
          </li>
          <li className="check-list__item">
            <span className="material-symbols-outlined">check</span>
            Complete equipment set
          </li>
          <li className="check-list__item">
            <span className="material-symbols-outlined">check</span>
            Instructor guidance
          </li>
          <li className="check-list__item">
            <span className="material-symbols-outlined">check</span>
            Take home your creation
          </li>
          <li className="check-list__item">
            <span className="material-symbols-outlined">check</span>
            3-hour session duration
          </li>
        </ul>
      </section>

      <section className="section section--combo" style={{ borderTop: '1px solid #eee' }}>
        <span className="activity__label">Activity 06</span>
        <h2 className="combo__heading">Paint & Pottery (Stoneware)</h2>
        <p className="combo__desc">
          Premium stoneware combo — create pottery and paint with underglaze technique, finished with transparent glaze. *Painting session is done after the first firing.
        </p>
        <div className="combo__box">
          <span className="combo__price">
            500K <span>/ pax</span>
          </span>
          <span className="combo__note">2 Sessions</span>
        </div>
        <ul className="check-list" style={{ marginTop: '2rem', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <li className="check-list__item">
            <span className="material-symbols-outlined">check</span>
            700g premium clay
          </li>
          <li className="check-list__item">
            <span className="material-symbols-outlined">check</span>
            Complete equipment set
          </li>
          <li className="check-list__item">
            <span className="material-symbols-outlined">check</span>
            Instructor guidance
          </li>
          <li className="check-list__item">
            <span className="material-symbols-outlined">check</span>
            Underglaze coloring
          </li>
          <li className="check-list__item">
            <span className="material-symbols-outlined">check</span>
            Transparent glaze (Food grade)
          </li>
          <li className="check-list__item">
            <span className="material-symbols-outlined">check</span>
            Take home your creation
          </li>
          <li className="check-list__item">
            <span className="material-symbols-outlined">check</span>
            3-hour session duration
          </li>
        </ul>
      </section>

      <section className="section section--combo" style={{ borderTop: '1px solid #eee' }}>
        <span className="activity__label">Activity 07</span>
        <h2 className="combo__heading">Pottery (Stoneware) & Painting (Earthenware)</h2>
        <p className="combo__desc">
          Combine the best of both worlds: create a stoneware pottery piece and paint an earthenware terracotta piece in one session.
        </p>
        <div className="combo__box">
          <span className="combo__price">
            450K <span>/ pax</span>
          </span>
          <span className="combo__note">3 Hours</span>
        </div>
        <ul className="check-list" style={{ marginTop: '2rem', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <li className="check-list__item">
            <span className="material-symbols-outlined">check</span>
            700g premium clay
          </li>
          <li className="check-list__item">
            <span className="material-symbols-outlined">check</span>
            1 terracotta media for painting
          </li>
          <li className="check-list__item">
            <span className="material-symbols-outlined">check</span>
            Complete equipment set
          </li>
          <li className="check-list__item">
            <span className="material-symbols-outlined">check</span>
            Instructor guidance
          </li>
          <li className="check-list__item">
            <span className="material-symbols-outlined">check</span>
            Transparent glaze for stoneware
          </li>
          <li className="check-list__item">
            <span className="material-symbols-outlined">check</span>
            Take home your creation
          </li>
          <li className="check-list__item">
            <span className="material-symbols-outlined">check</span>
            3-hour session duration
          </li>
        </ul>
      </section>

      {/* ── Sailing ──────────────────────────────────────── */}
      <section className="section section--activity">
        <div className="activity-grid">
          <div className="activity-grid__visual">
            <img
              className="activity__img"
              src="/images/sailing/sailing1.webp"
              alt="Sailing Trip"
            />
          </div>
          <div className="activity-grid__content">
            <span className="activity__label">Activity 08</span>
            <h2 className="activity__title">Sailing Trip</h2>
            <p className="activity__desc">
              Take a peaceful journey across Lake Tondano. Experience the gentle breeze and breathtaking views. *Min. payment for 5 people applies even for smaller groups.
            </p>
            <div className="activity__price-row" style={{ marginBottom: '1.5rem', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '1rem' }}>
              <span className="activity__price">
                50K <span>/ person</span>
              </span>
              <span className="activity__duration">20–30 Minutes</span>
              <span className="activity__duration" style={{ background: 'var(--accent-light)', color: 'var(--accent-dark)', border: '1px solid var(--accent-dark)' }}>Min. 5 Pax Charge · Max 15 Pax</span>
            </div>
            <ul className="check-list">
              <li className="check-list__item">
                <span className="material-symbols-outlined">check</span>
                20–30 minute duration
              </li>
              <li className="check-list__item">
                <span className="material-symbols-outlined">check</span>
                Lake Tondano tour
              </li>
              <li className="check-list__item">
                <span className="material-symbols-outlined">check</span>
                Life jackets and safety gear included
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* ── Kayak ────────────────────────────────────────── */}
      <section className="section section--activity">
        <div className="activity-grid activity-grid--reverse">
          <div className="activity-grid__content">
            <span className="activity__label">Activity 09</span>
            <h2 className="activity__title">Kayak</h2>
            <p className="activity__desc">
              Navigate the serene waters of Lake Tondano at your own pace. A perfect blend of light exercise and deep connection with nature.
            </p>
            <div className="activity__price-row" style={{ marginBottom: '1.5rem', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '1rem' }}>
              <span className="activity__price">
                275K <span>/ kayak</span>
              </span>
              <span className="activity__duration">30 Minutes</span>
              <span className="activity__duration" style={{ background: 'var(--accent-light)', color: 'var(--accent-dark)', border: '1px solid var(--accent-dark)' }}>Max 2 Persons</span>
            </div>
            <ul className="check-list">
              <li className="check-list__item">
                <span className="material-symbols-outlined">check</span>
                30 minute duration
              </li>
              <li className="check-list__item">
                <span className="material-symbols-outlined">check</span>
                Life jackets and safety gear included
              </li>
              <li className="check-list__item">
                <span className="material-symbols-outlined">check</span>
                Explore the lake at your own pace
              </li>
            </ul>
            <div style={{ marginTop: '1.5rem', display: 'flex', gap: '2rem' }}>
              <div>
                <span style={{ fontSize: '0.75rem', color: '#8B7355', fontWeight: 600, letterSpacing: '1px' }}>BEST TIME: MORNING</span>
                <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>05:30 AM</p>
              </div>
              <div>
                <span style={{ fontSize: '0.75rem', color: '#8B7355', fontWeight: 600, letterSpacing: '1px' }}>BEST TIME: AFTERNOON</span>
                <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>16:00 PM</p>
              </div>
            </div>
          </div>
          <div className="activity-grid__visual">
            <img
              className="activity__img"
              src="/images/kayak/kayak1.webp"
              alt="Kayak Experience"
            />
          </div>
        </div>
      </section>

      {/* ── Pre-Wedding ──────────────────────────────────── */}
      <section className="section section--prewedding">
        <div className="prewedding__header">
          <span className="activity__label">Bespoke Experience</span>
          <h2 className="activity__title">Photoshoot Package</h2>
        </div>
        <div className="prewedding__grid">
          <div>
            <p className="activity__desc" style={{ maxWidth: '100%', marginBottom: '2rem' }}>
              Capture your most precious moments with our professional photoshoot package. Enjoy exclusive access to all our scenic locations.
            </p>
            <div className="prewedding__price-row">
              <span className="prewedding__price">Rp 1.5 Juta</span>
              <span className="prewedding__duration">3 HOURS DURATION</span>
            </div>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '1.5rem', fontStyle: 'italic' }}>
              1 Hour Preparation · 2 Hours Photoshoot
            </p>
            <ul className="prewedding__features">
              {[
                'Small boat access',
                'Large boat access',
                'Pottery & painting session',
                'Restaurant photoshoot area',
                '3-hour duration (1h prep + 2h shoot)',
              ].map((f, i) => (
                <li key={i}>
                  <span className="material-symbols-outlined">radio_button_checked</span>
                  {f}
                </li>
              ))}
            </ul>
          </div>
          <div className="img-grid">
            <img
              className="img-grid__img"
              src="/images/prewed/prewed1.jpg"
              alt="Pre-wedding atmosphere"
            />
            <img
              className="img-grid__img img-grid__img--prewed2"
              src="/images/prewed/prewed2.jpg"
              alt="Pre-wedding details"
            />
          </div>
        </div>
      </section>

      {/* ── Essential Info ────────────────────────────────── */}
      <section className="section section--info">
        <h2 className="info__heading">
          <span className="material-symbols-outlined">info</span>
          Essential Information
        </h2>
        <div className="info__grid">
          {[
            {
              title: 'Punctuality',
              body: 'Please arrive on time. Late arrivals of more than 10 minutes will result in a forfeited booking as there are subsequent sessions.',
            },
            {
              title: 'Confirm & Refund',
              body: 'Bookings are confirmed after payment. Payments are non-refundable.',
            },
            {
              title: 'Reschedule',
              body: 'For rescheduling, please contact us at least 1 day (H-1) before the activity.',
            },
            {
              title: 'Invoice',
              body: 'After payment is completed, the invoice will be automatically sent to your WhatsApp number. Please ensure you provide an active WhatsApp number in the booking form.',
            },
          ].map((item) => (
            <div key={item.title}>
              <h4 className="info__item-title">{item.title}</h4>
              <p className="info__item-body">{item.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────────── */}
      <section className="section section--cta">
        <h2 className="cta__heading">Begin Your Journey</h2>
        <p className="cta__subtext">Your moment of lakeside serenity is just a reservation away.</p>
        <Link className="cta__btn" to="/reservation">
          Book Your Experience
        </Link>
      </section>

      <Footer />
    </>
  );
}